import { Fragment, type ReactNode } from "react";
import { Link } from "react-router-dom";

/**
 * The answer renderer.
 *
 * A deliberately small Markdown subset — paragraphs, headings, both list kinds, pipe
 * tables, bold, inline code and links — assembled into React elements. Nothing is ever
 * passed through `innerHTML`: the assistant reads tenant email and OCR'd invoices, so
 * its output is untrusted text by construction and must not be able to inject markup.
 *
 * Links matter more than the rest of the syntax. The model is instructed to cite
 * records as in-app routes, and rendering those as router links is what turns a stated
 * figure into something a Verwalter can open and check. Anything that is not a
 * relative path renders as plain text — a fabricated external URL is not a citation.
 */

export function Markierung({ text }: { text: string }) {
  return <div className="flex flex-col gap-[6px]">{bloecke(text)}</div>;
}

function bloecke(text: string): ReactNode[] {
  const zeilen = text.replace(/\r\n/g, "\n").split("\n");
  const ausgabe: ReactNode[] = [];
  let i = 0;

  while (i < zeilen.length) {
    const zeile = zeilen[i] ?? "";

    if (!zeile.trim()) {
      i += 1;
      continue;
    }

    const ueberschrift = /^(#{1,4})\s+(.*)$/.exec(zeile);
    if (ueberschrift) {
      ausgabe.push(
        <p
          key={`h-${i}`}
          className="text-[length:var(--text-sm)] mt-[4px]"
          style={{ color: "var(--text-primary)", fontWeight: "var(--weight-strong)" }}
        >
          {inline(ueberschrift[2] ?? "")}
        </p>,
      );
      i += 1;
      continue;
    }

    if (/^\s*\|.*\|\s*$/.test(zeile) && /^\s*\|[\s:|-]+\|\s*$/.test(zeilen[i + 1] ?? "")) {
      const zeilenBlock: string[] = [];
      while (i < zeilen.length && /^\s*\|.*\|\s*$/.test(zeilen[i] ?? "")) {
        zeilenBlock.push(zeilen[i] ?? "");
        i += 1;
      }
      ausgabe.push(<Tabelle key={`tab-${i}`} zeilen={zeilenBlock} />);
      continue;
    }

    if (/^\s*>\s?/.test(zeile)) {
      const zitat: string[] = [];
      while (i < zeilen.length && /^\s*>\s?/.test(zeilen[i] ?? "")) {
        zitat.push((zeilen[i] ?? "").replace(/^\s*>\s?/, ""));
        i += 1;
      }
      // Approval cards quote the exact wording that is about to be recorded. It has to
      // be visibly set apart from the sentence explaining it, or the signer cannot see
      // where the assistant's framing stops and the text they are signing begins.
      ausgabe.push(
        <blockquote
          key={`bq-${i}`}
          className="border-l-2 pl-[8px] py-[2px]"
          style={{ borderColor: "var(--line-strong)", background: "var(--surface-sunken)" }}
        >
          <p className="text-[length:var(--text-sm)]" style={zeilenStil}>
            {inline(zitat.join(" "))}
          </p>
        </blockquote>,
      );
      continue;
    }

    if (/^\s*[-*]\s+/.test(zeile)) {
      const punkte: string[] = [];
      while (i < zeilen.length && /^\s*[-*]\s+/.test(zeilen[i] ?? "")) {
        punkte.push((zeilen[i] ?? "").replace(/^\s*[-*]\s+/, ""));
        i += 1;
      }
      ausgabe.push(
        <ul key={`ul-${i}`} className="flex flex-col gap-[2px] pl-[8px]">
          {punkte.map((punkt, n) => (
            <li key={n} className="flex gap-[6px] text-[length:var(--text-sm)]" style={zeilenStil}>
              <span aria-hidden="true" style={{ color: "var(--text-muted)" }}>
                ·
              </span>
              <span>{inline(punkt)}</span>
            </li>
          ))}
        </ul>,
      );
      continue;
    }

    if (/^\s*\d+[.)]\s+/.test(zeile)) {
      const punkte: string[] = [];
      while (i < zeilen.length && /^\s*\d+[.)]\s+/.test(zeilen[i] ?? "")) {
        punkte.push((zeilen[i] ?? "").replace(/^\s*\d+[.)]\s+/, ""));
        i += 1;
      }
      ausgabe.push(
        <ol key={`ol-${i}`} className="flex flex-col gap-[2px] pl-[8px]">
          {punkte.map((punkt, n) => (
            <li key={n} className="flex gap-[6px] text-[length:var(--text-sm)]" style={zeilenStil}>
              <span className="mono shrink-0" style={{ color: "var(--text-muted)" }}>
                {n + 1}.
              </span>
              <span>{inline(punkt)}</span>
            </li>
          ))}
        </ol>,
      );
      continue;
    }

    const absatz: string[] = [];
    while (i < zeilen.length && (zeilen[i] ?? "").trim() && !istBlockstart(zeilen[i] ?? "")) {
      absatz.push(zeilen[i] ?? "");
      i += 1;
    }
    ausgabe.push(
      <p key={`p-${i}`} className="text-[length:var(--text-sm)]" style={zeilenStil}>
        {inline(absatz.join(" "))}
      </p>,
    );
  }

  return ausgabe;
}

const zeilenStil = { color: "var(--text-primary)", lineHeight: "var(--leading-text)" } as const;

function istBlockstart(zeile: string): boolean {
  return (
    /^(#{1,4})\s+/.test(zeile) ||
    /^\s*[-*]\s+/.test(zeile) ||
    /^\s*\d+[.)]\s+/.test(zeile) ||
    /^\s*>\s?/.test(zeile) ||
    /^\s*\|.*\|\s*$/.test(zeile)
  );
}

function Tabelle({ zeilen }: { zeilen: string[] }) {
  const zellen = zeilen.map((z) =>
    z
      .trim()
      .replace(/^\|/, "")
      .replace(/\|$/, "")
      .split("|")
      .map((c) => c.trim()),
  );
  const kopf = zellen[0] ?? [];
  const koerper = zellen.slice(2);

  return (
    <div className="overflow-x-auto">
      <table className="w-full border-collapse text-[length:var(--text-xs)]">
        <thead>
          <tr>
            {kopf.map((zelle, n) => (
              <th
                key={n}
                className="text-left px-[4px] py-[2px] border-b"
                style={{
                  color: "var(--text-caps)",
                  borderColor: "var(--line-hairline)",
                  fontWeight: "var(--weight-medium)",
                }}
              >
                {inline(zelle)}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {koerper.map((reihe, n) => (
            <tr key={n}>
              {reihe.map((zelle, m) => (
                <td
                  key={m}
                  className="px-[4px] py-[2px] align-top border-b"
                  style={{ color: "var(--text-primary)", borderColor: "var(--line-hairline)" }}
                >
                  {inline(zelle)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/** Bold, inline code and links, in one pass so a link inside bold still resolves. */
const INLINE = /(\*\*[^*]+\*\*)|(`[^`]+`)|(\[[^\]]+\]\([^)]+\))/g;

function inline(text: string): ReactNode {
  const teile: ReactNode[] = [];
  let zuletzt = 0;
  let treffer: RegExpExecArray | null;
  INLINE.lastIndex = 0;

  while ((treffer = INLINE.exec(text)) !== null) {
    if (treffer.index > zuletzt) teile.push(text.slice(zuletzt, treffer.index));
    const stueck = treffer[0];

    if (stueck.startsWith("**")) {
      teile.push(
        <strong key={teile.length} style={{ fontWeight: "var(--weight-medium)" }}>
          {stueck.slice(2, -2)}
        </strong>,
      );
    } else if (stueck.startsWith("`")) {
      teile.push(
        <code
          key={teile.length}
          className="mono px-[2px] rounded-[var(--radius-1)]"
          style={{ background: "var(--surface-sunken)", color: "var(--text-primary)" }}
        >
          {stueck.slice(1, -1)}
        </code>,
      );
    } else {
      const zerlegt = /^\[([^\]]+)\]\(([^)]+)\)$/.exec(stueck);
      const label = zerlegt?.[1] ?? stueck;
      const ziel = zerlegt?.[2] ?? "";
      teile.push(
        ziel.startsWith("/") ? (
          <Link
            key={teile.length}
            to={ziel}
            className="underline underline-offset-2"
            style={{ color: "var(--text-link)" }}
          >
            {label}
          </Link>
        ) : (
          <Fragment key={teile.length}>{label}</Fragment>
        ),
      );
    }

    zuletzt = treffer.index + stueck.length;
  }

  if (zuletzt < text.length) teile.push(text.slice(zuletzt));
  return teile;
}
